import React from 'react';
import {
  FormControl,
  FormLabel,
  FormLabelProps,
  Textarea,
  TextareaProps,
  forwardRef,
  FormErrorMessage,
  FormErrorMessageProps,
  InputGroup,
  FormControlProps,
  InputGroupProps,
} from '@chakra-ui/react';
import clsx from 'clsx';
import { disableScrollingOnce } from 'app/utils/utils';

interface FormLabelPropsWithTestId extends FormLabelProps {
  'data-testid': string;
}

interface FormErrorMessagePropsWithTestId extends FormErrorMessageProps {
  'data-testid': string;
}

export interface ChakraTextareaFieldProps extends TextareaProps {
  formControlStyleProps?: FormControlProps;
  placeholder?: string;
  label?: string;
  labelProps?: FormLabelPropsWithTestId;
  errorMessage?: string;
  errorMessageProps?: FormErrorMessagePropsWithTestId;
  inputGroupProps?: InputGroupProps;
}

export const ChakraTextareaField = forwardRef<
  ChakraTextareaFieldProps,
  'textarea'
>((props, ref) => {
  const {
    label,
    placeholder,
    errorMessage,
    labelProps,
    errorMessageProps,
    formControlStyleProps,
    inputGroupProps,
    // Following props are destructured because they are required
    // on FormControl element, and not on the Input element.
    id,
    isRequired,
    isDisabled,
    isInvalid,
    isReadOnly,
    ...rest
  } = props;

  const textareaElement = (
    <Textarea
      placeholder={placeholder || ' '}
      data-testid={label}
      // Prevent scroll into input on touch
      onTouchStart={(e) => {
        e.preventDefault();
        Object.assign(e.currentTarget.readOnly, true);
        disableScrollingOnce();
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        Object.assign(e.currentTarget.readOnly, false);
        e.currentTarget.focus({ preventScroll: true });
      }}
      isDisabled={isDisabled}
      {...rest}
      ref={ref}
    />
  );

  return (
    <FormControl
      variant="floating"
      {...{ id, isRequired, isDisabled, isInvalid, isReadOnly }}
      {...formControlStyleProps}
    >
      <InputGroup {...inputGroupProps}>
        {textareaElement}
        <FormLabel
          {...labelProps}
          className={clsx(labelProps?.className, {
            hasPlaceholder: Boolean(placeholder),
          })}
        >
          {label}
        </FormLabel>
      </InputGroup>

      {errorMessage && (
        <FormErrorMessage data-testid="error-text" {...errorMessageProps}>
          {errorMessage}
        </FormErrorMessage>
      )}
    </FormControl>
  );
});
